export type YouTubeEmbed = {
  provider: 'youtube';
  videoId: string;
  embedUrl: string;
};

export type LoomEmbed = {
  provider: 'loom';
  videoId: string;
  embedUrl: string;
};

export type XPostEmbed = {
  provider: 'x';
  postUrl: string;
};

export type ParsedEmbed = YouTubeEmbed | LoomEmbed | XPostEmbed;

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com'
]);
const YOUTUBE_SHORT_HOST = 'youtu.be';
const LOOM_HOSTS = new Set(['loom.com', 'www.loom.com']);
const X_HOSTS = new Set([
  'x.com',
  'www.x.com',
  'twitter.com',
  'www.twitter.com'
]);

// The privacy-enhanced host does not set cookies until the viewer presses play.
const YOUTUBE_EMBED_BASE_URL = 'https://www.youtube-nocookie.com/embed/';
const YOUTUBE_VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_PATH_PREFIXES = ['/embed/', '/shorts/', '/live/', '/v/'];
const X_STATUS_PATH_PATTERN = /^\/[A-Za-z0-9_]+\/status\/\d+/;
const LOOM_EMBED_BASE_URL = 'https://www.loom.com/embed/';
// Share and embed links both carry the recording's 32-character hex id.
const LOOM_PATH_PATTERN = /^\/(share|embed)\/([a-f0-9]{32})(\/|$)/;

const parseUrl = (value: string): URL | undefined => {
  try {
    return new URL(value.trim());
  } catch {
    return undefined;
  }
};

const youtubeVideoIdFrom = (url: URL): string | undefined => {
  if (url.hostname === YOUTUBE_SHORT_HOST) {
    return url.pathname.slice(1).split('/')[0];
  }
  if (!YOUTUBE_HOSTS.has(url.hostname)) return undefined;

  const queryId = url.searchParams.get('v');
  if (queryId) return queryId;

  const prefix = YOUTUBE_PATH_PREFIXES.find((candidate) =>
    url.pathname.startsWith(candidate)
  );
  if (!prefix) return undefined;
  return url.pathname.slice(prefix.length).split('/')[0];
};

/**
 * Turns the URL an editor pasted (or the import script found in an iframe)
 * into something the page can render. Anything unrecognised returns
 * undefined so the block renders nothing rather than an iframe to an
 * arbitrary host.
 */
export const parseEmbedUrl = (
  value: string | undefined
): ParsedEmbed | undefined => {
  if (!value) return undefined;
  const url = parseUrl(value);
  if (!url || url.protocol !== 'https:') return undefined;

  const videoId = youtubeVideoIdFrom(url);
  if (videoId && YOUTUBE_VIDEO_ID_PATTERN.test(videoId)) {
    return {
      provider: 'youtube',
      videoId,
      embedUrl: `${YOUTUBE_EMBED_BASE_URL}${videoId}`
    };
  }

  const loomMatch = LOOM_HOSTS.has(url.hostname)
    ? LOOM_PATH_PATTERN.exec(url.pathname)
    : null;
  if (loomMatch) {
    const [, , loomId] = loomMatch;
    return {
      provider: 'loom',
      videoId: loomId,
      embedUrl: `${LOOM_EMBED_BASE_URL}${loomId}`
    };
  }

  if (X_HOSTS.has(url.hostname) && X_STATUS_PATH_PATTERN.test(url.pathname)) {
    const [, handle, , statusId] = url.pathname.split('/');
    return {
      provider: 'x',
      postUrl: `https://x.com/${handle}/status/${statusId}`
    };
  }

  return undefined;
};
